import * as i0 from '@angular/core';
import { EventEmitter, forwardRef, Component, Input, Output, ViewChild, HostListener } from '@angular/core';
import * as i2 from '@angular/forms';
import { NG_VALUE_ACCESSOR, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { v4 } from 'uuid';
import { getFormControlFrom, isRequired } from '@elotech/angular-components/utils';
import * as i1 from '@angular/common';
import { CommonModule } from '@angular/common';
import * as i3 from '@elotech/angular-components/directives';
import { DirectivesModule } from '@elotech/angular-components/directives';
import { distinctUntilChanged, tap, debounceTime } from 'rxjs';
import { InfiniteScrollDirective } from 'ngx-infinite-scroll';

class SelectComponent {
    set options(options) {
        this._options = options;
        this.items = this.transformToItems(options);
        this.reTransformSelectedItems();
        if (!this.open)
            this.resetSearchValue();
    }
    get options() {
        return this._options;
    }
    onTab() {
        if (this.indexHighlighted >= 0) {
            this.select(this.getItemsFiltered()[this.indexHighlighted]);
        }
        this.resetSearchValue();
        this.open = false;
    }
    onEnter() {
        if (this.indexHighlighted >= 0) {
            this.select(this.getItemsFiltered()[this.indexHighlighted]);
            this.open = false;
        }
    }
    onArrowUp() {
        if (this.indexHighlighted > 0) {
            this.indexHighlighted = this.indexHighlighted - 1;
            this.scrollToHighlighted();
        }
    }
    onArrowDown() {
        if (this.indexHighlighted < this.getItemsFiltered().length - 1) {
            this.indexHighlighted = this.indexHighlighted + 1;
            this.scrollToHighlighted();
        }
    }
    constructor(injector, ref) {
        this.injector = injector;
        this.ref = ref;
        this._options = [];
        this.required = false;
        this.multiple = false;
        this.placeholder = 'Selecione o item';
        this.label = '';
        this.optionLabelFn = (obj) => obj;
        this.optionValueFn = (obj) => obj;
        this.onSelect = new EventEmitter();
        this.optionsPageableFn = null;
        this.loadOptionsPageableOnTouch = false;
        this.optionsPageableThrottle = 500;
        this.loadingOptionsPageable = false;
        this.emitterLoadOptionsPageable = new EventEmitter();
        this.optionsPageableCurrentPage = 0;
        this.allowRemoveValue = true;
        this.items = [];
        this.itemsSelected = [];
        this.indexHighlighted = -1;
        this.searchValue = '';
        this.open = false;
        this.id = v4();
        this.touched = false;
        this.disabled = false;
        this.onChange = (value) => { };
        this.onTouched = () => { };
        this.selectIdentify = (index, item) => item?.key;
    }
    ngOnChanges(changes) {
        if (this.options) {
            this.options = this.options;
        }
    }
    ngOnInit() {
        this.formControl = getFormControlFrom(this.injector);
        this.configOptionsPageable();
    }
    getItemsFiltered() {
        return this.optionsPageableFn ? this.items : this.items.filter(item => !this.searchValue || item.key.toLowerCase().includes(this.searchValue.toLowerCase()));
    }
    isRequired() {
        return this.required || isRequired(this.formControl);
    }
    getInputState() {
        if (this.loadingOptionsPageable) {
            return {
                icon: 'fa fa-circle-notch fa-spin',
                action: () => { }
            };
        }
        if (this.open) {
            return {
                icon: 'fa fa-angle-up',
                action: () => {
                    this.open = false;
                    this.resetSearchValue();
                }
            };
        }
        if (this.itemsSelected.length && this.allowRemoveValue) {
            return {
                icon: 'fa fa-times',
                action: () => {
                    this.itemsSelected = [];
                    this.emitChanges();
                }
            };
        }
        return {
            icon: 'fa fa-angle-down',
            action: () => this.open = true
        };
    }
    resetSearchValue() {
        this.indexHighlighted = -1;
        if (this.itemsSelected.length) {
            this.searchValue = (this.multiple && this.itemsSelected.length > 1 ? `(${this.itemsSelected.length}) ` : '') + this.itemsSelected.map(i => i.key).join(', ');
        }
        else {
            this.searchValue = '';
        }
    }
    toItem(value) {
        const option = this.options.find(x => (this.optionValue ? (x[this.optionValue] || this.getNestedObject(x, this.optionValue)) : this.optionValueFn(x)) == value) || value;
        const key = (this.optionLabel ? (option[this.optionLabel] || this.getNestedObject(option, this.optionLabel)) : this.optionLabelFn(option));
        const val = (this.optionValue ? (option[this.optionValue] || this.getNestedObject(option, this.optionValue)) : this.optionValueFn(option));
        return {
            key: key != null && key != undefined ? key : 'Carregando...',
            value: val != null && val != undefined ? val : option
        };
    }
    getNestedObject(obj, key) {
        return key.split(".").reduce(function (o, x) {
            return (typeof o == "undefined" || o === null) ? o : o[x];
        }, obj);
    }
    reTransformSelectedItems() {
        this.itemsSelected = this.itemsSelected.map(item => this.toItem(item.value));
        setTimeout(() => {
            this.ref.detectChanges();
        }, 0);
    }
    transformToItems(options) {
        if (options.length) {
            return this.options.map(option => (this.toItem(option)));
        }
        return [];
    }
    isItemSelected(item) {
        return this.itemsSelected.map(x => x.key).includes(item.key);
    }
    isAllSelected() {
        return this.itemsSelected.length === this.items.length;
    }
    selectAll() {
        setTimeout(() => {
            this.itemsSelected = [...this.items];
            this.emitChanges();
        }, 0);
    }
    unSelectAll() {
        setTimeout(() => {
            this.itemsSelected = [];
            this.emitChanges();
        }, 0);
    }
    select(item) {
        if (this.itemsSelected.includes(item)) {
            if (this.multiple) {
                this.itemsSelected.splice(this.itemsSelected.indexOf(item), 1);
                this.emitChanges();
            }
        }
        else {
            if (!this.multiple) {
                this.itemsSelected = [];
            }
            this.itemsSelected.push(item);
            this.emitChanges();
        }
    }
    scrollToHighlighted() {
        const listHalfHeight = this.itemList.nativeElement.getBoundingClientRect().height / 2;
        const itemOffsetHeight = Number(Array.from(this.itemList.nativeElement.querySelectorAll(".br-item"))
            .filter((el) => el.id < this.indexHighlighted)
            .reduce((acc, el) => acc + el.offsetHeight, 0));
        if (itemOffsetHeight > listHalfHeight) {
            this.itemList.nativeElement.scrollTo(0, itemOffsetHeight - listHalfHeight + 50, { behavior: 'smooth' });
        }
        else {
            this.itemList.nativeElement.scrollTo(0, 0, { behavior: 'smooth' });
        }
    }
    emitChanges() {
        this.resetSearchValue();
        const emitValue = this.itemsSelected.map(i => i.value);
        if (this.multiple) {
            this.onChange(emitValue);
        }
        else {
            this.onChange(emitValue.length ? emitValue[0] : null);
        }
        this.onTouched();
        this.onSelect.emit(this.itemsSelected);
    }
    registerOnChange(fn) {
        this.onChange = fn;
    }
    registerOnTouched(fn) {
        this.onTouched = fn;
    }
    writeValue(obj) {
        if (obj !== null && obj !== undefined) {
            if (this.multiple) {
                this.itemsSelected = obj.map((o) => this.toItem(o));
            }
            else {
                this.itemsSelected = [this.toItem(obj)];
            }
        }
        else {
            this.itemsSelected = [];
        }
        this.resetSearchValue();
    }
    setDisabledState(isDisabled) {
        this.disabled = isDisabled;
    }
    configOptionsPageable() {
        this.emitterLoadOptionsPageable.pipe(distinctUntilChanged((a, b) => a == b), tap(() => {
            if (this.optionsPageableFn) {
                this.loadingOptionsPageable = true;
                this.optionsPageableCurrentPage = 0;
                this.options = [];
            }
        }), debounceTime(this.optionsPageableThrottle)).subscribe(search => {
            this.executeOptionsFnIfExists(search, 0);
        });
        if (!this.loadOptionsPageableOnTouch) {
            this.emitterLoadOptionsPageable.emit('');
        }
    }
    executeOptionsFnNextPage() {
        this.executeOptionsFnIfExists(this.searchValue, ++this.optionsPageableCurrentPage);
    }
    executeOptionsFnIfExists(search, page) {
        if (this.optionsPageableFn) {
            this.loadingOptionsPageable = true;
            this.optionsPageableFn(search, page).subscribe(options => {
                this.loadingOptionsPageable = false;
                this.options = page == 0 ? options : [...this.options, ...options];
                if (page == 0) {
                    this.itemList.nativeElement.scrollTo(0, 0);
                }
            });
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: SelectComponent, deps: [{ token: i0.Injector }, { token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.12", type: SelectComponent, isStandalone: true, selector: "elo-select", inputs: { options: "options", required: "required", multiple: "multiple", placeholder: "placeholder", label: "label", optionLabel: "optionLabel", optionValue: "optionValue", optionLabelFn: "optionLabelFn", optionValueFn: "optionValueFn", optionsPageableFn: "optionsPageableFn", loadOptionsPageableOnTouch: "loadOptionsPageableOnTouch", allowRemoveValue: "allowRemoveValue" }, outputs: { onSelect: "onSelect" }, host: { listeners: { "keydown.Tab": "onTab()", "keydown.Enter": "onEnter()", "keydown.ArrowUp": "onArrowUp()", "keydown.ArrowDown": "onArrowDown()" } }, providers: [
            {
                provide: NG_VALUE_ACCESSOR,
                multi: true,
                useExisting: forwardRef(() => SelectComponent),
            }
        ], viewQueries: [{ propertyName: "itemList", first: true, predicate: ["itemList"], descendants: true }], usesOnChanges: true, ngImport: i0, template: "<div (clickOutside)=\"open = false; resetSearchValue()\" [class.form-group]=\"label\" style=\"position: relative;\">\n\n  <label *ngIf=\"label\" class=\"label\" for=\"field\">{{label}}</label>\n\n  <input type=\"text\" class=\"has-input-icon\" #searchInput [disabled]=\"disabled\" [required]=\"isRequired()\"\n    [(ngModel)]=\"searchValue\"\n    (ngModelChange)=\"indexHighlighted = -1; this.open ? emitterLoadOptionsPageable.emit($event): ''\"\n    (focus)=\"open = true; searchValue = ''\" type=\"text\" [placeholder]=\"placeholder\">\n  <div (click)=\"inputState.action.call(this)\" *ngIf=\"getInputState(); let inputState\" class=\"input-icon\" style=\"display: flex; justify-content: center;\n      align-items: center;\">\n    <i class=\"{{inputState.icon}}\"></i>\n  </div>\n\n  <div #itemList infiniteScroll [infiniteScrollDistance]=\"2\" [infiniteScrollThrottle]=\"optionsPageableThrottle\"\n    (scrolled)=\"this.executeOptionsFnNextPage()\" [scrollWindow]=\"false\" class=\"autocomplete\" [class.open]=\"open\"\n    style=\"--spacing-scale-2x: 9px; z-index: 999;\">\n    <div *ngIf=\"multiple && this.items?.length\" [class.selected]=\"isAllSelected()\" class=\"autocomplete-item\"\n      (click)=\"isAllSelected() ? unSelectAll(): selectAll(); ; $event.preventDefault();\">\n      <div class=\"inputSelect\">\n        <input id=\"cb\" name=\"cb\" type=\"checkbox\" [checked]=\"isAllSelected()\">\n        <label for=\"cb\">{{isAllSelected() ? 'Deselecionar Todos' : 'Selecionar Todos'}}</label>\n      </div>\n    </div>\n\n    <div class=\"my-2\" *ngIf=\"getItemsFiltered().length == 0\">\n      <div class=\"'br-radio'\" style=\"padding: 5px;\">\n        <label class=\"m-2\" style=\"font-size: 13px;\">{{loadingOptionsPageable ? 'Buscando...' :\n          'Nenhum item encontrado.'}}</label>\n      </div>\n    </div>\n\n    <div *ngFor=\"let item of getItemsFiltered(); let index = index; trackBy:selectIdentify\" class=\"autocomplete-item\"\n      [id]=\"index\" [class.bg-gray-20]=\"indexHighlighted == index\"\n      (click)=\"select(item); open = false; $event.preventDefault();\" [class.selected]=\"isItemSelected(item)\">\n\n      <div class=\"inputSelect\">\n        <input [id]=\"index\" [type]=\"multiple ? 'checkbox' : 'radio'\" [checked]=\"isItemSelected(item)\" />\n        <label [class.p-2]=\"!multiple\" [for]=\"index\">{{item.key}}</label>\n      </div>\n\n    </div>\n  </div>\n</div>", styles: ["input.ng-valid[required]{background-image:unset!important}.br-select{max-width:100%;min-width:auto}.br-select .br-list{max-height:250px}label{cursor:pointer}.app-list{display:none;margin-top:-2px;max-height:404px;overflow:auto;position:absolute;resize:none;width:100%;z-index:1}.app-list[expanded]{display:block}.inputSelect input{opacity:0;width:0}.autocomplete-item:hover,.autocomplete-item.selected{background:#cfcfcf;text-shadow:none!important}.fa-angle-down,.fa-angle-up{font-weight:700}\n"], dependencies: [{ kind: "ngmodule", type: CommonModule }, { kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "ngmodule", type: FormsModule }, { kind: "directive", type: i2.DefaultValueAccessor, selector: "input:not([type=checkbox])[formControlName],textarea[formControlName],input:not([type=checkbox])[formControl],textarea[formControl],input:not([type=checkbox])[ngModel],textarea[ngModel],[ngDefaultControl]" }, { kind: "directive", type: i2.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i2.RequiredValidator, selector: ":not([type=checkbox])[required][formControlName],:not([type=checkbox])[required][formControl],:not([type=checkbox])[required][ngModel]", inputs: ["required"] }, { kind: "directive", type: i2.NgModel, selector: "[ngModel]:not([formControlName]):not([formControl])", inputs: ["name", "disabled", "ngModel", "ngModelOptions"], outputs: ["ngModelChange"], exportAs: ["ngModel"] }, { kind: "ngmodule", type: ReactiveFormsModule }, { kind: "directive", type: InfiniteScrollDirective, selector: "[infiniteScroll], [infinite-scroll], [data-infinite-scroll]", inputs: ["infiniteScrollDistance", "infiniteScrollUpDistance", "infiniteScrollThrottle", "infiniteScrollDisabled", "infiniteScrollContainer", "scrollWindow", "immediateCheck", "horizontal", "alwaysCallback", "fromRoot"], outputs: ["scrolled", "scrolledUp"] }, { kind: "ngmodule", type: DirectivesModule }, { kind: "directive", type: i3.ClickOutsideDirective, selector: "[clickOutside]", inputs: ["ignoreClickSelector"], outputs: ["clickOutside"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: SelectComponent, decorators: [{
            type: Component,
            args: [{ selector: 'elo-select', imports: [
                        CommonModule,
                        FormsModule,
                        ReactiveFormsModule,
                        InfiniteScrollDirective,
                        DirectivesModule,
                    ], standalone: true, providers: [
                        {
                            provide: NG_VALUE_ACCESSOR,
                            multi: true,
                            useExisting: forwardRef(() => SelectComponent),
                        }
                    ], template: "<div (clickOutside)=\"open = false; resetSearchValue()\" [class.form-group]=\"label\" style=\"position: relative;\">\n\n  <label *ngIf=\"label\" class=\"label\" for=\"field\">{{label}}</label>\n\n  <input type=\"text\" class=\"has-input-icon\" #searchInput [disabled]=\"disabled\" [required]=\"isRequired()\"\n    [(ngModel)]=\"searchValue\"\n    (ngModelChange)=\"indexHighlighted = -1; this.open ? emitterLoadOptionsPageable.emit($event): ''\"\n    (focus)=\"open = true; searchValue = ''\" type=\"text\" [placeholder]=\"placeholder\">\n  <div (click)=\"inputState.action.call(this)\" *ngIf=\"getInputState(); let inputState\" class=\"input-icon\" style=\"display: flex; justify-content: center;\n      align-items: center;\">\n    <i class=\"{{inputState.icon}}\"></i>\n  </div>\n\n  <div #itemList infiniteScroll [infiniteScrollDistance]=\"2\" [infiniteScrollThrottle]=\"optionsPageableThrottle\"\n    (scrolled)=\"this.executeOptionsFnNextPage()\" [scrollWindow]=\"false\" class=\"autocomplete\" [class.open]=\"open\"\n    style=\"--spacing-scale-2x: 9px; z-index: 999;\">\n    <div *ngIf=\"multiple && this.items?.length\" [class.selected]=\"isAllSelected()\" class=\"autocomplete-item\"\n      (click)=\"isAllSelected() ? unSelectAll(): selectAll(); ; $event.preventDefault();\">\n      <div class=\"inputSelect\">\n        <input id=\"cb\" name=\"cb\" type=\"checkbox\" [checked]=\"isAllSelected()\">\n        <label for=\"cb\">{{isAllSelected() ? 'Deselecionar Todos' : 'Selecionar Todos'}}</label>\n      </div>\n    </div>\n\n    <div class=\"my-2\" *ngIf=\"getItemsFiltered().length == 0\">\n      <div class=\"'br-radio'\" style=\"padding: 5px;\">\n        <label class=\"m-2\" style=\"font-size: 13px;\">{{loadingOptionsPageable ? 'Buscando...' :\n          'Nenhum item encontrado.'}}</label>\n      </div>\n    </div>\n\n    <div *ngFor=\"let item of getItemsFiltered(); let index = index; trackBy:selectIdentify\" class=\"autocomplete-item\"\n      [id]=\"index\" [class.bg-gray-20]=\"indexHighlighted == index\"\n      (click)=\"select(item); open = false; $event.preventDefault();\" [class.selected]=\"isItemSelected(item)\">\n\n      <div class=\"inputSelect\">\n        <input [id]=\"index\" [type]=\"multiple ? 'checkbox' : 'radio'\" [checked]=\"isItemSelected(item)\" />\n        <label [class.p-2]=\"!multiple\" [for]=\"index\">{{item.key}}</label>\n      </div>\n\n    </div>\n  </div>\n</div>", styles: ["input.ng-valid[required]{background-image:unset!important}.br-select{max-width:100%;min-width:auto}.br-select .br-list{max-height:250px}label{cursor:pointer}.app-list{display:none;margin-top:-2px;max-height:404px;overflow:auto;position:absolute;resize:none;width:100%;z-index:1}.app-list[expanded]{display:block}.inputSelect input{opacity:0;width:0}.autocomplete-item:hover,.autocomplete-item.selected{background:#cfcfcf;text-shadow:none!important}.fa-angle-down,.fa-angle-up{font-weight:700}\n"] }]
        }], ctorParameters: () => [{ type: i0.Injector }, { type: i0.ChangeDetectorRef }], propDecorators: { options: [{
                type: Input
            }], required: [{
                type: Input
            }], multiple: [{
                type: Input
            }], placeholder: [{
                type: Input
            }], label: [{
                type: Input
            }], optionLabel: [{
                type: Input
            }], optionValue: [{
                type: Input
            }], optionLabelFn: [{
                type: Input
            }], optionValueFn: [{
                type: Input
            }], onSelect: [{
                type: Output
            }], itemList: [{
                type: ViewChild,
                args: ['itemList']
            }], optionsPageableFn: [{
                type: Input
            }], loadOptionsPageableOnTouch: [{
                type: Input
            }], allowRemoveValue: [{
                type: Input
            }], onTab: [{
                type: HostListener,
                args: ['keydown.Tab']
            }], onEnter: [{
                type: HostListener,
                args: ['keydown.Enter']
            }], onArrowUp: [{
                type: HostListener,
                args: ['keydown.ArrowUp']
            }], onArrowDown: [{
                type: HostListener,
                args: ['keydown.ArrowDown']
            }] } });

/**
 * Generated bundle index. Do not edit.
 */

export { SelectComponent };
